import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Button,
  Box,
  ButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
  getDoc,
  doc,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../api/firebase";

import MailOutlineIcon from "@mui/icons-material/MailOutline"; // メール済
import TaskAltIcon from "@mui/icons-material/TaskAlt"; // 応募済
import StarIcon from "@mui/icons-material/Star";
import { Chip } from "@mui/material";

const PAGE_SIZE = 500;

const regionMap = {
  "北海道・東北": [
    "北海道",
    "青森県",
    "岩手県",
    "宮城県",
    "秋田県",
    "山形県",
    "福島県",
  ],
  関東: [
    "茨城県",
    "栃木県",
    "群馬県",
    "埼玉県",
    "千葉県",
    "東京都",
    "神奈川県",
  ],
  中部: [
    "新潟県",
    "富山県",
    "石川県",
    "福井県",
    "山梨県",
    "長野県",
    "岐阜県",
    "静岡県",
    "愛知県",
  ],
  近畿: [
    "三重県",
    "滋賀県",
    "京都府",
    "大阪府",
    "兵庫県",
    "奈良県",
    "和歌山県",
  ],
  中国: ["鳥取県", "島根県", "岡山県", "広島県", "山口県"],
  四国: ["徳島県", "香川県", "愛媛県", "高知県"],
  "九州・沖縄": [
    "福岡県",
    "佐賀県",
    "長崎県",
    "熊本県",
    "大分県",
    "宮崎県",
    "鹿児島県",
    "沖縄県",
  ],
  その他: ["その他海外", "海外(アメリカ)"],
};

const prefectureCounts = {
  東京都: 5411,
  神奈川県: 3087,
  大阪府: 2851,
  愛知県: 2315,
  兵庫県: 1884,
  福岡県: 1862,
  埼玉県: 1847,
  千葉県: 1597,
  北海道: 1531,
  沖縄県: 1014,
  静岡県: 940,
  京都府: 885,
  広島県: 791,
  宮城県: 702,
  長野県: 659,
  茨城県: 654,
  その他海外: 616,
  岐阜県: 572,
  岡山県: 553,
  熊本県: 522,
  新潟県: 519,
  群馬県: 439,
  鹿児島県: 418,
  三重県: 413,
  栃木県: 411,
  奈良県: 398,
  滋賀県: 380,
  "海外(アメリカ)": 358,
  山口県: 344,
  大分県: 341,
  石川県: 341,
  福島県: 321,
  長崎県: 321,
  愛媛県: 292,
  山梨県: 273,
  富山県: 271,
  宮崎県: 265,
  香川県: 262,
  岩手県: 238,
  佐賀県: 233,
  山形県: 225,
  和歌山県: 221,
  青森県: 208,
  島根県: 181,
  福井県: 168,
  徳島県: 158,
  鳥取県: 155,
  秋田県: 144,
  高知県: 131,
};

const groupColors = [
  "#e3f2fd",
  "#fce4ec",
  "#f3e5f5",
  "#f1f8e9",
  "#fff8e1",
  "#ede7f6",
  "#e0f7fa",
  "#ffebee",
  "#f9fbe7",
  "#e8f5e9",
];

const categorizePrefecture = (pref) => {
  for (const [region, list] of Object.entries(regionMap)) {
    if (list.includes(pref)) return region;
  }
  return "その他";
};

export default function PrefectureCountList() {
  const [selectedPrefecture, setSelectedPrefecture] = useState("");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const [userNameCache, setUserNameCache] = useState({});

  const [filterMode, setFilterMode] = useState("all"); // 'all' | 'commented' | 'mailed' | 'applied'
  const [countStats, setCountStats] = useState({
    total: null,
    commented: null,
    mailed: null,
    applied: null,
  });
  const handleChange = (event) => {
    const value = event.target.value;
    setSelectedPrefecture(value);
    setDocs([]);
    setLastDoc(null);
    setHasMore(false);
  };

  useEffect(() => {
    if (!selectedPrefecture) return;

    // 件数だけの軽量クエリ
    const fetchCounts = async () => {
      const baseQ = query(
        collection(db, "ReiNoInori"),
        where("prefecture", "==", selectedPrefecture),
        where("wantAdvice", "==", true)
      );

      // 総件数
      const total = (await getCountFromServer(baseQ)).data().count;

      // コメント付き
      const commentedQ = query(baseQ, where("commentedBy", ">", ""));
      const commented = (await getCountFromServer(commentedQ)).data().count;

      // ★ メール済
      const mailedQ = query(baseQ, where("mailedBy", ">", ""));
      const mailed = (await getCountFromServer(mailedQ)).data().count;

      // ★ 応募済
      const appliedQ = query(baseQ, where("applicant", ">", ""));
      const applied = (await getCountFromServer(appliedQ)).data().count;

      setCountStats({ total, commented, mailed, applied });
    };

    setCountStats({ total: null, commented: null }); // 🔄 ローディング用
    fetchCounts();
  }, [selectedPrefecture]);

  // ---------- 一覧取得用 useEffect ----------
  useEffect(() => {
    if (!selectedPrefecture) return;

    // 一覧用 state をリセット
    setDocs([]);
    setLastDoc(null);
    setHasMore(false);

    // 最新の filterMode を反映して取得
    fetchDocs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPrefecture, filterMode]);

  // ===============================================
  //  一覧を取得するヘルパー関数
  //  ・isFirst === true  ⇒ page リセット & 最初の PAGE_SIZE 件取得
  //  ・isFirst === false ⇒ 続きを取得（lastDoc 以降）
  // ===============================================
  const fetchDocs = async (isFirst = false) => {
    if (!selectedPrefecture) return;

    setLoading(true);
    try {
      // ---------- 1) 基本クエリ ----------
      let clauses = [
        collection(db, "ReiNoInori"),
        where("prefecture", "==", selectedPrefecture),
        where("wantAdvice", "==", true),
      ];

      // ---------- 2) フィルターモードごとの追加 ----------
      switch (filterMode) {
        case "commented":
          clauses.push(where("commentedBy", ">", ""));
          clauses.push(orderBy("commentedBy")); // range フィルタの列を先頭に
          clauses.push(orderBy("name")); // 二次ソート
          break;

        case "mailed":
          clauses.push(where("mailedBy", ">", ""));
          clauses.push(orderBy("mailedBy"));
          clauses.push(orderBy("name"));
          break;

        case "applied":
          clauses.push(where("applicant", ">", ""));
          clauses.push(orderBy("applicant"));
          clauses.push(orderBy("name"));
          break;

        default:
          // "all"
          clauses.push(orderBy("name"));
      }

      clauses.push(limit(PAGE_SIZE));
      let q = query(...clauses);

      // ---------- 3) ページング ----------
      if (!isFirst && lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      // ---------- 4) Firestore 取得 ----------
      const snap = await getDocs(q);
      const newDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDocs((prev) => [...(isFirst ? [] : prev), ...newDocs]);

      // ---------- 5) UID → 名前キャッシュ ----------
      //  (commentedBy / mailedBy / applicant すべて対象)
      const uidFields = ["commentedBy", "mailedBy", "applicant"];
      const missingUids = [
        ...new Set(
          uidFields
            .flatMap((f) => newDocs.map((d) => d[f]))
            .filter((uid) => uid && !userNameCache[uid])
        ),
      ];

      if (missingUids.length) {
        const userSnaps = await Promise.all(
          missingUids.map((uid) => getDoc(doc(db, "users", uid)))
        );
        const nameMap = {};
        userSnaps.forEach((s) => {
          if (s.exists()) nameMap[s.id] = s.data().name || "(名前未設定)";
        });
        setUserNameCache((prev) => ({ ...prev, ...nameMap }));
      }

      // ---------- 6) ページング状態更新 ----------
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.size === PAGE_SIZE);
    } catch (err) {
      console.error("Firestore取得エラー:", err);
    } finally {
      setLoading(false);
    }
  };

  // グループ順ソート
  const sortedDocs = [...docs].sort((a, b) => {
    const ga = a.reviewGroupId || "";
    const gb = b.reviewGroupId || "";
    return ga.localeCompare(gb);
  });

  const groupIdList = [
    ...new Set(sortedDocs.map((doc) => doc.reviewGroupId || "未設定")),
  ];
  const groupIdToColor = groupIdList.reduce((acc, id, i) => {
    acc[id] = groupColors[i % groupColors.length];
    return acc;
  }, {});

  const groupedPrefectures = {};
  Object.entries(prefectureCounts).forEach(([pref, count]) => {
    const region = categorizePrefecture(pref);
    if (!groupedPrefectures[region]) groupedPrefectures[region] = [];
    groupedPrefectures[region].push({ name: pref, count });
  });

  const commentedBg = "#fff3e0"; // オレンジ系の薄いハイライト
  const defaultBg = "transparent";

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        都道府県別 DDP 表示
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="prefecture-select-label">都道府県を選択</InputLabel>
        <Select
          labelId="prefecture-select-label"
          value={selectedPrefecture}
          label="都道府県を選択"
          onChange={handleChange}
        >
          {Object.entries(groupedPrefectures).map(([region, prefs]) => [
            <ListSubheader key={region}>{region}</ListSubheader>,
            prefs.map(({ name, count }) => (
              <MenuItem key={name} value={name}>
                {name}（{count}件）
              </MenuItem>
            )),
          ])}
        </Select>
      </FormControl>

      {/* ★ フィルタートグル */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <ButtonGroup variant="outlined" size="small">
            <Button
              onClick={() => setFilterMode("all")}
              variant={filterMode === "all" ? "contained" : "outlined"}
            >
              すべて（{countStats.total ?? "…"}件）
            </Button>
            <Button
              onClick={() => setFilterMode("commented")}
              variant={filterMode === "commented" ? "contained" : "outlined"}
            >
              コメントあり（{countStats.commented ?? "…"}件）
            </Button>
            <Button
              onClick={() => setFilterMode("mailed")}
              variant={filterMode === "mailed" ? "contained" : "outlined"}
            >
              メール済（{countStats.mailed ?? "…"}件）
            </Button>
            <Button
              onClick={() => setFilterMode("applied")}
              variant={filterMode === "applied" ? "contained" : "outlined"}
            >
              応募済（{countStats.applied ?? "…"}件）
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      {loading && docs.length === 0 && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <CircularProgress />
        </div>
      )}

      {!loading && docs.length === 0 && selectedPrefecture && (
        <Typography>データが見つかりませんでした。</Typography>
      )}

      {sortedDocs.map((d, i) => {
        const isCommented = !!d.comment || !!d.commentedBy;

        return (
          <Accordion
            key={d.id}
            sx={{
              bgcolor: isCommented ? commentedBg : defaultBg, // ★ここ
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {/* 左側：タイトル文字列  */}
              <Typography fontWeight="bold" sx={{ flexGrow: 1 }}>
                {i + 1}. {d.name} ({d.mail}) {d.prefecture}
                <span
                  style={{
                    background: groupIdToColor[d.reviewGroupId || "未設定"],
                    padding: "2px 6px",
                    borderRadius: 4,
                    marginLeft: 8,
                  }}
                >
                  {d.reviewGroupId || "未設定"}
                </span>
              </Typography>

              {/* 右側：状態アイコン（必要なものだけ表示） */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {/* メール済 */}
                {!!d.mailedBy && (
                  <MailOutlineIcon
                    fontSize="small"
                    color="primary" // ← 色はお好みで
                    titleAccess="メール済"
                  />
                )}
                {/* 応募済 */}
                {!!d.applicant && (
                  <TaskAltIcon
                    fontSize="small"
                    sx={{ color: "success.main" }} // success 色で “完了” 感を演出
                    titleAccess="応募済"
                  />
                )}
                {typeof d.rating === "number" && (
                  <>
                    <StarIcon
                      fontSize="small"
                      sx={{ color: "warning.main" }} // 常に黄色で “評価有り” を示す
                      titleAccess={`Rating: ${d.rating}`}
                    />
                  </>
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ bgcolor: "background.default" }}>
              {/* --- DDP --- */}
              <Box
                sx={{
                  borderLeft: 4,
                  borderColor: "primary.main",
                  bgcolor: "grey.100",
                  p: 2,
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="bold"
                >
                  DDP
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {d.ddp || "（未設定）"}
                </Typography>
              </Box>

              {/* --- コメント --- */}
              <Box
                sx={{
                  bgcolor: "warning.light",
                  p: 2,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="bold"
                >
                  コメント
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "pre-wrap", mb: 1 }}
                >
                  {d.comment || "（未記入）"}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  コメント担当:&nbsp;
                  {d.commentedBy
                    ? userNameCache[d.commentedBy] || d.commentedBy
                    : "（未記入）"}
                </Typography>
                {/* ⭐ rating があれば評価ラベルを表示 */}
                {typeof d.rating === "number" && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={
                        {
                          1: "大変参考になった",
                          2: "より詳しく聞きたい",
                          3: "質問をしてみたい",
                          4: "気づきがあった",
                          5: "ありがとう",
                        }[d.rating]
                      }
                      color={
                        {
                          1: "primary",
                          2: "secondary",
                          3: "warning",
                          4: "info",
                          5: "success",
                        }[d.rating]
                      }
                      icon={<StarIcon />}
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Button
            variant="contained"
            onClick={() => fetchDocs(false)}
            disabled={loading}
          >
            {loading ? "読み込み中..." : "もっと見る"}
          </Button>
        </div>
      )}
    </Container>
  );
}
